import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/adminCommunity.css";

const API_BASE_URL = "https://naxor.naxora.net";

const AdminCommunity = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("tickets");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [tickets, setTickets] = useState([]);
  const [communityMessages, setCommunityMessages] = useState([]);
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementMessage, setAnnouncementMessage] = useState("");
  const [announcementPinned, setAnnouncementPinned] = useState(false);
  const [permissionUserId, setPermissionUserId] = useState("");
  const [permissionNote, setPermissionNote] = useState("");
  const [manualCommunityAccess, setManualCommunityAccess] = useState(false);
  const [communityBlocked, setCommunityBlocked] = useState(false);
  const [replyInputs, setReplyInputs] = useState({});
  const [statusInputs, setStatusInputs] = useState({});
  const [internalNoteInputs, setInternalNoteInputs] = useState({});
  const [announcements, setAnnouncements] = useState([]);

  const token = useMemo(() => {
    return localStorage.getItem("token") || localStorage.getItem("naxoraToken");
  }, []);

  const authHeaders = useMemo(() => {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [token]);

  const showMessage = (text) => {
    setMessage(text || "");
    if (text) {
      setTimeout(() => {
        setMessage("");
      }, 3500);
    }
  };

  const handleUnauthorized = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("naxoraToken");
    navigate("/login");
  };

  const requestApi = async (endpoint, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...authHeaders,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401) {
      handleUnauthorized();
      return null;
    }

    if (!response.ok) {
      throw new Error(data.message || "Request failed");
    }

    return data;
  };

  const loadAdminCommunityData = async () => {
    try {
      setLoading(true);

      const [ticketsData, messagesData, announcementsData] = await Promise.all([
        requestApi("/api/admin-community/support/tickets"),
        requestApi("/api/admin-community/community/messages"),
        requestApi("/api/community/announcements"),
      ]);

      if (Array.isArray(ticketsData?.tickets)) {
        setTickets(ticketsData.tickets);
      }

      if (Array.isArray(messagesData?.messages)) {
        setCommunityMessages(messagesData.messages);
      }

      if (Array.isArray(announcementsData?.announcements)) {
        setAnnouncements(announcementsData.announcements);
      }
    } catch (error) {
      showMessage(error.message || "Failed to load admin community data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    loadAdminCommunityData();
  }, [token]);

  const formatDate = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusLabel = (status) => {
    const value = String(status || "").replace(/_/g, " ");

    if (!value) return "Open";

    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const replyToTicket = async (ticketId) => {
    const cleanMessage = String(replyInputs[ticketId] || "").trim();

    if (!cleanMessage) {
      showMessage("Please enter admin reply");
      return;
    }

    try {
      setActionLoading(true);

      const data = await requestApi(
        `/api/admin-community/support/tickets/${ticketId}/reply`,
        {
          method: "POST",
          body: JSON.stringify({
            message: cleanMessage,
          }),
        }
      );

      if (data?.ticket) {
        setTickets((prev) =>
          prev.map((ticket) =>
            String(ticket.id) === String(ticketId) ? data.ticket : ticket
          )
        );

        setReplyInputs((prev) => ({
          ...prev,
          [ticketId]: "",
        }));

        showMessage("Admin reply sent successfully");
      }
    } catch (error) {
      showMessage(error.message || "Failed to send admin reply");
    } finally {
      setActionLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId) => {
    const cleanStatus = String(statusInputs[ticketId] || "").trim();
    const cleanInternalNote = String(internalNoteInputs[ticketId] || "").trim();

    if (!cleanStatus) {
      showMessage("Please select ticket status");
      return;
    }

    try {
      setActionLoading(true);

      const data = await requestApi(
        `/api/admin-community/support/tickets/${ticketId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({
            status: cleanStatus,
            internalNote: cleanInternalNote,
          }),
        }
      );

      if (data?.ticket) {
        setTickets((prev) =>
          prev.map((ticket) =>
            String(ticket.id) === String(ticketId) ? data.ticket : ticket
          )
        );

        setStatusInputs((prev) => ({
          ...prev,
          [ticketId]: "",
        }));

        setInternalNoteInputs((prev) => ({
          ...prev,
          [ticketId]: "",
        }));

        showMessage("Ticket status updated successfully");
      }
    } catch (error) {
      showMessage(error.message || "Failed to update ticket status");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteCommunityMessage = async (messageId) => {
    try {
      setActionLoading(true);

      await requestApi(`/api/admin-community/community/messages/${messageId}`, {
        method: "DELETE",
      });

      setCommunityMessages((prev) =>
        prev.map((item) =>
          String(item.id) === String(messageId)
            ? {
                ...item,
                deleted: true,
              }
            : item
        )
      );

      showMessage("Community message deleted successfully");
    } catch (error) {
      showMessage(error.message || "Failed to delete community message");
    } finally {
      setActionLoading(false);
    }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();

    const cleanTitle = announcementTitle.trim();
    const cleanMessage = announcementMessage.trim();

    if (!cleanTitle || !cleanMessage) {
      showMessage("Title and message are required");
      return;
    }

    try {
      setActionLoading(true);

      const data = await requestApi("/api/admin-community/community/announcements", {
        method: "POST",
        body: JSON.stringify({
          title: cleanTitle,
          message: cleanMessage,
          pinned: announcementPinned,
        }),
      });

      if (data?.announcement) {
        setAnnouncements((prev) => [data.announcement, ...prev]);
        setAnnouncementTitle("");
        setAnnouncementMessage("");
        setAnnouncementPinned(false);
        showMessage("Announcement created successfully");
      }
    } catch (error) {
      showMessage(error.message || "Failed to create announcement");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    try {
      setActionLoading(true);

      await requestApi(
        `/api/admin-community/community/announcements/${announcementId}`,
        {
          method: "DELETE",
        }
      );

      setAnnouncements((prev) =>
        prev.filter((announcement) => String(announcement.id) !== String(announcementId))
      );

      showMessage("Announcement deleted successfully");
    } catch (error) {
      showMessage(error.message || "Failed to delete announcement");
    } finally {
      setActionLoading(false);
    }
  };

  const updateCommunityPermission = async (e) => {
    e.preventDefault();

    const cleanUserId = permissionUserId.trim();

    if (!cleanUserId) {
      showMessage("User ID is required");
      return;
    }

    try {
      setActionLoading(true);

      await requestApi(`/api/admin-community/community/permission/${cleanUserId}`, {
        method: "PATCH",
        body: JSON.stringify({
          manualCommunityAccess,
          communityBlocked,
          note: permissionNote.trim(),
        }),
      });

      setPermissionUserId("");
      setPermissionNote("");
      setManualCommunityAccess(false);
      setCommunityBlocked(false);
      showMessage("Community permission updated successfully");
    } catch (error) {
      showMessage(error.message || "Failed to update community permission");
    } finally {
      setActionLoading(false);
    }
  };

  const manualReviewTickets = tickets.filter(
    (ticket) =>
      ticket.needsSeniorAdmin === true ||
      String(ticket.status || "") === "manual_review"
  );

  const visibleCommunityMessages = communityMessages.filter(
    (item) => item.deleted !== true
  );

  const renderHeader = () => {
    return (
      <div className="admin-community-header">
        <button
          type="button"
          className="admin-community-back"
          onClick={() => navigate("/admin")}
        >
          ←
        </button>

        <div>
          <h1>Admin Community Control</h1>
          <p>Manage support tickets, leader community messages, announcements, and manual access.</p>
        </div>
      </div>
    );
  };

  const renderTabs = () => {
    return (
      <div className="admin-community-tabs">
        <button
          type="button"
          className={activeTab === "tickets" ? "active" : ""}
          onClick={() => setActiveTab("tickets")}
        >
          Tickets
        </button>

        <button
          type="button"
          className={activeTab === "manual" ? "active" : ""}
          onClick={() => setActiveTab("manual")}
        >
          Manual Review
        </button>

        <button
          type="button"
          className={activeTab === "messages" ? "active" : ""}
          onClick={() => setActiveTab("messages")}
        >
          Messages
        </button>

        <button
          type="button"
          className={activeTab === "announcements" ? "active" : ""}
          onClick={() => setActiveTab("announcements")}
        >
          Announcements
        </button>

        <button
          type="button"
          className={activeTab === "permissions" ? "active" : ""}
          onClick={() => setActiveTab("permissions")}
        >
          Permissions
        </button>
      </div>
    );
  };

  const renderTicketCard = (ticket) => {
    return (
      <div className="admin-ticket-card" key={ticket.id}>
        <div className="admin-ticket-top">
          <div>
            <h3>{ticket.subject || "Support Request"}</h3>
            <span>{getStatusLabel(ticket.status)}</span>
            {ticket.needsSeniorAdmin && (
              <strong>Senior Admin Review Required</strong>
            )}
          </div>

          <small>{formatDate(ticket.createdAt)}</small>
        </div>

        <div className="admin-ticket-user">
          <span>User</span>
          <strong>{ticket.userName || "User"}</strong>
          <small>{ticket.userEmail || ""}</small>
          <small>ID: {ticket.userId || ""}</small>
        </div>

        <div className="admin-ticket-messages">
          {Array.isArray(ticket.messages) &&
            ticket.messages.map((item) => (
              <div key={item.id} className={`admin-ticket-message ${item.sender}`}>
                <div>
                  <strong>
                    {item.sender === "user"
                      ? "User"
                      : item.sender === "ai"
                      ? "NAXORA AI"
                      : "Admin"}
                  </strong>
                  <small>{formatDate(item.createdAt)}</small>
                </div>
                <p>{item.message}</p>
              </div>
            ))}
        </div>

        <div className="admin-ticket-actions">
          <textarea
            value={replyInputs[ticket.id] || ""}
            onChange={(e) =>
              setReplyInputs((prev) => ({
                ...prev,
                [ticket.id]: e.target.value,
              }))
            }
            placeholder="Write admin reply..."
            maxLength={1000}
          />

          <button
            type="button"
            onClick={() => replyToTicket(ticket.id)}
            disabled={actionLoading}
          >
            Send Reply
          </button>
        </div>

        <div className="admin-ticket-status-row">
          <select
            value={statusInputs[ticket.id] || ""}
            onChange={(e) =>
              setStatusInputs((prev) => ({
                ...prev,
                [ticket.id]: e.target.value,
              }))
            }
          >
            <option value="">Select status</option>
            <option value="open">Open</option>
            <option value="manual_review">Manual Review</option>
            <option value="admin_replied">Admin Replied</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="rejected">Rejected</option>
          </select>

          <input
            type="text"
            value={internalNoteInputs[ticket.id] || ""}
            onChange={(e) =>
              setInternalNoteInputs((prev) => ({
                ...prev,
                [ticket.id]: e.target.value,
              }))
            }
            placeholder="Internal note"
            maxLength={500}
          />

          <button
            type="button"
            onClick={() => updateTicketStatus(ticket.id)}
            disabled={actionLoading}
          >
            Update
          </button>
        </div>
      </div>
    );
  };

  const renderTickets = () => {
    return (
      <div className="admin-community-card">
        <div className="admin-section-title">
          <h2>All Support Tickets</h2>
          <p>Review user issues and reply from admin side.</p>
        </div>

        <div className="admin-ticket-list">
          {tickets.length === 0 ? (
            <div className="admin-empty">No support tickets available.</div>
          ) : (
            tickets.map(renderTicketCard)
          )}
        </div>
      </div>
    );
  };

  const renderManualReview = () => {
    return (
      <div className="admin-community-card">
        <div className="admin-section-title">
          <h2>Senior Admin Manual Review</h2>
          <p>Tickets requiring manual review and approval decisions.</p>
        </div>

        <div className="admin-ticket-list">
          {manualReviewTickets.length === 0 ? (
            <div className="admin-empty">No manual review tickets available.</div>
          ) : (
            manualReviewTickets.map(renderTicketCard)
          )}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    return (
      <div className="admin-community-card">
        <div className="admin-section-title">
          <h2>Community Messages</h2>
          <p>Manage visible leader community messages.</p>
        </div>

        <div className="admin-message-list">
          {visibleCommunityMessages.length === 0 ? (
            <div className="admin-empty">No community messages available.</div>
          ) : (
            visibleCommunityMessages.map((item) => (
              <div className="admin-message-card" key={item.id}>
                <div className="admin-message-top">
                  <div>
                    <h3>{item.senderName || "User"}</h3>
                    <span>{item.senderBadge || "Member"}</span>
                    <small>ID: {item.senderId || ""}</small>
                  </div>

                  <small>{formatDate(item.createdAt)}</small>
                </div>

                <p>{item.message}</p>

                <button
                  type="button"
                  onClick={() => deleteCommunityMessage(item.id)}
                  disabled={actionLoading}
                >
                  Delete Message
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderAnnouncements = () => {
    return (
      <div className="admin-community-card">
        <div className="admin-section-title">
          <h2>Announcements</h2>
          <p>Create and manage official NAXORA announcements.</p>
        </div>

        <form className="admin-form" onSubmit={createAnnouncement}>
          <input
            type="text"
            value={announcementTitle}
            onChange={(e) => setAnnouncementTitle(e.target.value)}
            placeholder="Announcement title"
            maxLength={120}
          />

          <textarea
            value={announcementMessage}
            onChange={(e) => setAnnouncementMessage(e.target.value)}
            placeholder="Announcement message"
            maxLength={1000}
          />

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={announcementPinned}
              onChange={(e) => setAnnouncementPinned(e.target.checked)}
            />
            <span>Pin announcement</span>
          </label>

          <button type="submit" disabled={actionLoading}>
            Create Announcement
          </button>
        </form>

        <div className="admin-announcement-list">
          {announcements.length === 0 ? (
            <div className="admin-empty">No announcements available.</div>
          ) : (
            announcements.map((item) => (
              <div className="admin-announcement-card" key={item.id}>
                <div>
                  <h3>{item.title}</h3>
                  {item.pinned && <span>Pinned</span>}
                  <small>{formatDate(item.createdAt)}</small>
                </div>

                <p>{item.message}</p>

                <button
                  type="button"
                  onClick={() => deleteAnnouncement(item.id)}
                  disabled={actionLoading}
                >
                  Delete Announcement
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderPermissions = () => {
    return (
      <div className="admin-community-card">
        <div className="admin-section-title">
          <h2>Manual Community Permission</h2>
          <p>Give manual message access or block a user from community chat.</p>
        </div>

        <form className="admin-form" onSubmit={updateCommunityPermission}>
          <input
            type="text"
            value={permissionUserId}
            onChange={(e) => setPermissionUserId(e.target.value)}
            placeholder="User ID"
          />

          <textarea
            value={permissionNote}
            onChange={(e) => setPermissionNote(e.target.value)}
            placeholder="Admin note"
            maxLength={500}
          />

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={manualCommunityAccess}
              onChange={(e) => setManualCommunityAccess(e.target.checked)}
            />
            <span>Allow manual community message access</span>
          </label>

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={communityBlocked}
              onChange={(e) => setCommunityBlocked(e.target.checked)}
            />
            <span>Block community message access</span>
          </label>

          <button type="submit" disabled={actionLoading}>
            Update Permission
          </button>
        </form>
      </div>
    );
  };

  const renderActiveTab = () => {
    if (activeTab === "tickets") return renderTickets();
    if (activeTab === "manual") return renderManualReview();
    if (activeTab === "messages") return renderMessages();
    if (activeTab === "announcements") return renderAnnouncements();
    if (activeTab === "permissions") return renderPermissions();

    return renderTickets();
  };

  return (
    <div className="admin-community-page">
      {renderHeader()}

      {message && <div className="admin-community-toast">{message}</div>}

      {renderTabs()}

      {loading ? (
        <div className="admin-community-loading">Loading admin community control...</div>
      ) : (
        renderActiveTab()
      )}
    </div>
  );
};

export default AdminCommunity;